import React, { useContext, useEffect, useState } from "react";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from "@mui/material";
import { DateTime } from "luxon";
import mainStyles from "styles";

import { TemplateMetadata } from "@safetyculture/s12-apis-connect-web/s12/templates/v1/template_library_pb";
import { Pagination } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import useStyles from "./styles";
import { searchPublicLibrary } from "api/endpoints/search";
import { deletePublicTemplate } from "api/endpoints/templates-inspections";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import RPCTablePaginationActions from "components/shared/table/rpc-table-pagination-actions";
import { DEFAULT_REGION_CODE } from "constants/index";
import { SnackbarContext } from "context/snackbar-context";
import { convertTimestampToDate } from "helpers/time";

const PublicLibrarySearch: React.FC = () => {
  const classes = mainStyles();
  const plSearchClasses = useStyles();

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<
    TemplateMetadata[] | undefined
  >([]);
  const [searchResultsTotalHits, setSearchResultsTotalHits] = useState(0);

  const [showDeleteTemplate, setShowDeleteTemplate] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<
    TemplateMetadata | undefined
  >();

  const [searchRanking, setSearchRanking] = useState(
    "relevance_freshness_popularity"
  );

  const [slugFilterQuery, setSlugFilterQuery] = useState("");
  const [showSlugFilterDisclaimer, setShowSlugFilterDisclaimer] =
    useState(false);
  const [slugFilterDisclaimerDismissed, setSlugFilterDisclaimerDismissed] =
    useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const [paginationValues, setPaginationValues] = useState<Pagination>(
    new Pagination({
      pageSize: 25,
      offset: 0,
    })
  );

  const { showError } = useContext(SnackbarContext);

  useEffect(() => {
    searchTemplatesByQuery(
      searchQuery,
      searchRanking,
      paginationValues.offset,
      paginationValues.pageSize
    );
    // Adding functions in dep array would cause infinite re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchRanking]);

  const searchTemplatesByQuery = (
    query: string,
    ranking: string,
    pageNum: number,
    pageSize: number
  ) => {
    setIsLoading(true);
    //  Page num is incremented by 1 since for all other services page ( offset ) starts at 0 but for searchPublicTemplates it starts at 1.
    searchPublicLibrary(
      {
        query,
        ranking,
        pageNum: pageNum + 1,
        pageSize: pageSize,
      },
      callOptionsHeaders(DEFAULT_REGION_CODE)
    )
      .then((res) => {
        const results =
          res.searchTemplatesResults?.templateMetadataSearchResults;
        const totalHits = res.searchTemplatesResults?.totalHits || 0;
        setSearchResultsTotalHits(totalHits);
        setSearchResults(results);
        templateTablePopulator();
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        showError(error);
      });
  };

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPaginationValues({
      pageSize: paginationValues.pageSize,
      offset: newPage,
    } as Pagination);

    searchTemplatesByQuery(
      searchQuery,
      searchRanking,
      newPage,
      paginationValues.pageSize
    );
  };

  const handleSetSearchRanking = (sortCriteria: string) => {
    if (searchRanking === sortCriteria)
      setSearchRanking("relevance_freshness_popularity");
    else setSearchRanking(sortCriteria);
  };

  const deleteTemplateById = (templateId: string) => {
    setIsLoading(true);

    deletePublicTemplate(
      {
        templateId,
      },
      callOptionsHeaders(DEFAULT_REGION_CODE)
    )
      .then(() => {
        setIsLoading(false);
        localStorage.setItem(
          templateId,
          JSON.stringify({ type: "deletedTemplateId", templateId })
        );
        setSelectedTemplate(undefined);
      })
      .catch((error) => {
        setIsLoading(false);
        showError(error);
      });
  };

  const templateTablePopulator = () => {
    let filteredResults = searchResults;
    // filter to hide recently deleted
    Object.keys(localStorage).forEach(function (key) {
      const currentItem = localStorage.getItem(key);
      if (
        key.includes("template_") &&
        JSON.parse(currentItem || "").type === "deletedTemplateId"
      ) {
        const toDelete = JSON.parse(currentItem || "").templateId;
        filteredResults = filteredResults?.filter(
          (template) => template.templateId != toDelete
        );
      }
    });
    // filter based on slug
    if (slugFilterQuery != "") {
      filteredResults = filteredResults?.filter(
        (template) => template.productSlug.includes(slugFilterQuery) == true
      );
    }

    if (filteredResults != undefined && filteredResults.length) {
      return filteredResults.map((template, index) => {
        const createdAtDateString = template?.createdAt
          ? DateTime.fromJSDate(
              convertTimestampToDate(template.createdAt)
            ).toFormat("dd MMM yyyy")
          : "N/A";

        return (
          <TableRow key={index}>
            <TableCell data-testid="template-row-title">
              {template.title}
            </TableCell>
            <TableCell>{template.author}</TableCell>
            <TableCell>{template.downloads}</TableCell>
            <TableCell>{createdAtDateString}</TableCell>
            <TableCell>{template.templateId}</TableCell>
            <TableCell>
              <Button
                sx={{
                  ...plSearchClasses.tableButtonInner,
                  ...plSearchClasses.redText,
                }}
                data-testid="delete-template-btn"
                onClick={() => {
                  setSelectedTemplate(template);
                  setShowDeleteTemplate(true);
                }}
              >
                Delete
              </Button>
            </TableCell>
          </TableRow>
        );
      });
    } else
      return (
        <TableRow>
          <TableCell colSpan={7}>
            <b>{"No results found."}</b>
          </TableCell>
        </TableRow>
      );
  };

  return (
    <div>
      <Box sx={plSearchClasses.spaceSearchContainer}>
        <Box
          sx={{
            ...classes.spaceSearch,
            ...classes.spaceBottom,
            ...plSearchClasses.spaceSearchQueryContainer,
          }}
        >
          <TextField
            sx={plSearchClasses.queryTextField}
            id="search-query-input"
            label="Template Title"
            data-testid="public-template-search-input"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            size="small"
            onKeyUp={(event) => {
              if (event.key === "Enter") {
                searchTemplatesByQuery(
                  searchQuery,
                  searchRanking,
                  paginationValues.offset,
                  paginationValues.pageSize
                );
              }
            }}
          />
          <Button
            variant="contained"
            color="primary"
            data-testid="public-template-search-btn"
            onClick={() => {
              searchTemplatesByQuery(
                searchQuery,
                searchRanking,
                paginationValues.offset,
                paginationValues.pageSize
              );
            }}
          >
            Search
          </Button>
        </Box>
        <span style={{ position: "relative" }}>
          <Collapse
            in={showSlugFilterDisclaimer}
            timeout={500}
            sx={plSearchClasses.slugFilterDisclaimer}
          >
            <Alert
              severity="info"
              onClose={() => {
                setShowSlugFilterDisclaimer(false);
                setSlugFilterDisclaimerDismissed(true);
              }}
            >
              Only filtering current page
            </Alert>
          </Collapse>
          <TextField
            sx={plSearchClasses.slugFilterTextField}
            id="slug-filter-query-input"
            label="Slug filter"
            inputProps={{ "data-testid": "public-template-slug-filter-input" }}
            value={slugFilterQuery}
            onChange={(event) => {
              setSlugFilterQuery(event.target.value);
              if (!slugFilterDisclaimerDismissed) {
                setShowSlugFilterDisclaimer(true);
              }
            }}
            size="small"
          />
        </span>
      </Box>

      <Box sx={{ display: "flex" }}>
        <ConfirmDialog
          title={`Permanently delete '${selectedTemplate?.title}'?`}
          data-testid="confirm-delete-dialog"
          open={showDeleteTemplate}
          setOpen={setShowDeleteTemplate}
          onConfirm={() => {
            deleteTemplateById(selectedTemplate?.templateId || "");
          }}
          onCancel={() => {
            setSelectedTemplate(undefined);
          }}
          cancelBtnLabel="Cancel"
          confirmBtnLabel="Delete"
        >
          <Box sx={plSearchClasses.dialogInner}>
            <div>
              <strong>Author: </strong>
              {selectedTemplate?.author}
            </div>
            <div>
              <strong>Downloads: </strong>
              {selectedTemplate?.downloads}
            </div>
            <div>
              <strong>Template slug: </strong>
              {selectedTemplate?.productSlug}
            </div>
          </Box>
        </ConfirmDialog>
        <TableContainer component={Paper} style={{ flexBasis: "100%" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Author</TableCell>
                <TableCell width={140}>
                  <Button
                    sx={plSearchClasses.searchFilterButton}
                    style={
                      searchRanking === "downloads" ? { color: "blue" } : {}
                    }
                    data-testid="sort-by-downloads-btn"
                    onClick={() => {
                      handleSetSearchRanking("downloads");
                    }}
                  >
                    Downloads
                    <Box
                      component="span"
                      sx={plSearchClasses.rankingCaretAndCancel}
                    >
                      {searchRanking === "downloads" ? "✖" : "▼"}
                    </Box>
                  </Button>
                </TableCell>
                <TableCell width={140}>
                  <Button
                    sx={plSearchClasses.searchFilterButton}
                    style={
                      searchRanking === "date_published"
                        ? { color: "blue" }
                        : {}
                    }
                    data-testid="sort-by-date-published-btn"
                    onClick={() => {
                      handleSetSearchRanking("date_published");
                    }}
                  >
                    Created
                    <Box
                      component="span"
                      sx={plSearchClasses.rankingCaretAndCancel}
                    >
                      {searchRanking === "date_published" ? "✖" : "▼"}
                    </Box>
                  </Button>
                </TableCell>
                <TableCell>Template ID</TableCell>
                <TableCell align="center">Delete</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} style={{ textAlign: "center" }}>
                    <CircularProgress
                      color="primary"
                      variant="indeterminate"
                      thickness={3}
                      size={30}
                    />
                  </TableCell>
                </TableRow>
              ) : (
                templateTablePopulator()
              )}
            </TableBody>
            <TableFooter>
              <TablePagination
                rowsPerPageOptions={[paginationValues.pageSize]}
                count={searchResultsTotalHits}
                rowsPerPage={paginationValues.pageSize}
                page={paginationValues.offset}
                component="td"
                onPageChange={handleChangePage}
                ActionsComponent={RPCTablePaginationActions}
              />
            </TableFooter>
          </Table>
        </TableContainer>
      </Box>
    </div>
  );
};

export default PublicLibrarySearch;
